/**
 * EditorJS block shapes as stored by the CMS (head-site-admin).
 *
 * Mirrors the tool registry in head-site-admin `src/components/editor-js/block-tools-registry.ts`
 * and the per-tool data interfaces in `src/components/editor-js/block-tools/*.vue`.
 * Optional fields are optional because live content predates them (checked against a production
 * database copy, see `dev/editor-js-audit/`).
 *
 * Adding a tool to the CMS means adding it here and to the body renderer registry; an unknown
 * block type makes the renderer throw.
 */

export const BLOCK_TYPES = [
    'paragraph',
    'header',
    'list',
    'table',
    'delimiter',
    'image',
    'imagegallery',
    'quotetool',
    'highlighttool',
    'codeblocktool',
    'embed',
    'faq',
    'downloadtool',
    'ctaoneproducttool',
    'ctatwoproductstool',
    'ctathreeproductstool',
    'highlightproductcard',
    'relatedarticles',
    'caseStudyLeftBlock',
    'caseStudyLeftLogo',
    'columns',
    // legacy, no CMS tool any more and never rendered; its content is to be migrated in a separate ticket
    'quote',
] as const;

export type BlockType = typeof BLOCK_TYPES[number];

export interface StoredFile {
    url: string;
}

export interface ParagraphData {
    text: string;
}

export type HeaderLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface HeaderData {
    text: string;
    /** the CMS offers 2–6; level 1 survives in migrated content */
    level: HeaderLevel;
}

export interface HeaderTunes {
    anchorTune?: { anchor: string };
}

/** Legacy content stores plain strings; current content stores objects (`meta` arrived later). */
export type ListItem = string | {
    content: string;
    items?: ListItem[];
    meta?: Record<string, unknown>;
};

export interface ListData {
    /** `checklist` is hidden in the CMS but present in old content; it renders as an unordered list */
    style: 'ordered' | 'unordered' | 'checklist';
    items: ListItem[];
    meta?: { start?: number; counterType?: string };
}

export interface TableData {
    content: string[][];
    withHeadings?: boolean;
    stretched?: boolean;
}

export type DelimiterData = Record<string, never>;

export interface ImageData {
    file: StoredFile;
    caption: string;
    withBorder: boolean;
    stretched: boolean;
    withBackground: boolean;
    enlarge?: boolean;
}

export interface ImageGalleryData {
    /** storage hashes */
    files: string[];
}

export interface QuoteToolData {
    text: string;
    author: string;
    title?: string;
    details?: string;
    is_bold?: boolean;
    file?: StoredFile;
}

export interface HighlightToolLabel {
    text: string;
    type: string;
    variant: 'info' | 'success' | 'neutral';
    layout: 'solid' | 'subtle';
    size: 's' | 'l';
    theme: 'light' | 'dark';
    glyph: string | null;
}

export interface HighlightToolData {
    /** HTML; may contain markdown-style links `[text](url)` */
    text: string;
    alignment?: 'center' | 'left';
    is_labeled?: boolean;
    label?: HighlightToolLabel;
}

export interface CodeBlockToolData {
    code: string;
    /** empty in migrated content */
    language?: string;
}

export const EMBED_SERVICES = ['youtube', 'iframe'] as const;

export type EmbedService = typeof EMBED_SERVICES[number];

export interface EmbedData {
    /** legacy content also holds services the site cannot embed (slideshare, wistia, …): those render nothing */
    service: EmbedService | (string & {});
    /** YouTube video id */
    id?: string;
    iframeUrl?: string;
    /** legacy services */
    link?: string;
}

export interface FaqQuestion {
    question: string;
    /** HTML */
    answer: string;
}

export interface FaqData {
    title: string;
    lead: string;
    schemaEnabled: boolean;
    questions: FaqQuestion[];
}

export interface ButtonLink {
    to: string;
    text: string;
}

export interface DownloadToolData {
    text: string;
    button: ButtonLink & { type?: string };
}

export interface CtaProductCard {
    title: string;
    text: string;
    labelTarget: string;
    size?: '' | 'small';
    links: {
        tryLink: ButtonLink;
        learnLink: ButtonLink;
    };
}

export interface CtaProductsData {
    lead: string;
    cards: CtaProductCard[];
}

export interface HighlightProductCardData {
    file: StoredFile;
    type: 'regular' | 'super';
    label_title: string;
    label_text: string;
    target_url: string;
    button_text: string;
}

export interface RelatedArticlesData {
    isAutomated: boolean;
    articles: { id: string | null }[];
}

export interface CaseStudyLeftBlockData {
    title: string;
    items: string[];
}

export interface CaseStudyLeftLogoData {
    file: StoredFile;
}

export interface LegacyQuoteData {
    text: string;
    caption?: string;
    alignment?: string;
}

export interface ColumnsData {
    cols: { blocks: EditorJsBlock[]; time?: number; version?: string }[];
}

export interface BlockDataMap {
    paragraph: ParagraphData;
    header: HeaderData;
    list: ListData;
    table: TableData;
    delimiter: DelimiterData;
    image: ImageData;
    imagegallery: ImageGalleryData;
    quotetool: QuoteToolData;
    highlighttool: HighlightToolData;
    codeblocktool: CodeBlockToolData;
    embed: EmbedData;
    faq: FaqData;
    downloadtool: DownloadToolData;
    ctaoneproducttool: CtaProductCard;
    ctatwoproductstool: CtaProductsData;
    ctathreeproductstool: CtaProductsData;
    highlightproductcard: HighlightProductCardData;
    relatedarticles: RelatedArticlesData;
    caseStudyLeftBlock: CaseStudyLeftBlockData;
    caseStudyLeftLogo: CaseStudyLeftLogoData;
    columns: ColumnsData;
    quote: LegacyQuoteData;
}

export interface BlockTunesMap {
    header: HeaderTunes;
}

export interface EditorJsBlockOf<T extends BlockType> {
    /** missing on a handful of legacy blocks */
    id?: string;
    type: T;
    data: BlockDataMap[T];
    tunes?: T extends keyof BlockTunesMap ? BlockTunesMap[T] : Record<string, unknown>;
}

export type EditorJsBlock = { [T in BlockType]: EditorJsBlockOf<T> }[BlockType];

/** The envelope EditorJS saves; Word2JSON stores the bare `blocks` array instead. */
export interface EditorJsBody {
    time?: number;
    version?: string;
    blocks: EditorJsBlock[];
}

/** Visual context a body is rendered in; each keeps the look its page had before the renderers were unified. */
export type BodyVariant = 'blog' | 'tru' | 'resource' | 'legal' | 'event-about' | 'event-compact';
